import { useState } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Megaphone, Plus, Pencil, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

type Announcement = {
  _id: string;
  headline: string;
  content: string | null;
  linkUrl: string | null;
  linkText: string | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export default function Announcements() {
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    headline: "",
    content: "",
    linkUrl: "",
    linkText: "",
    isEnabled: true,
  });

  const { data: announcements = [] } = useQuery<Announcement[]>({
    queryKey: ["/api/admin/announcements"],
  });

  const resetForm = () => {
    setFormData({ headline: "", content: "", linkUrl: "", linkText: "", isEnabled: true });
    setEditingAnnouncement(null);
    setIsDialogOpen(false);
  };

  const createMutation = useApiMutation({
    endpoint: "/api/admin/announcements",
    method: "POST",
    invalidate: [["/api/admin/announcements"]],
    successMessage: "Announcement created successfully",
    errorMessage: "Failed to create announcement",
    onSuccess: resetForm,
  });

  const updateMutation = useApiMutation({
    endpoint: (vars: { id: string; data?: any }) => `/api/admin/announcements/${vars.id}`,
    method: "PATCH",
    invalidate: [["/api/admin/announcements"]],
    successMessage: "Announcement updated successfully",
    errorMessage: "Failed to update announcement",
    onSuccess: resetForm,
  });

  const deleteMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/announcements/${vars.id}`,
    method: "DELETE",
    invalidate: [["/api/admin/announcements"]],
    successMessage: "Announcement deleted successfully",
    errorMessage: "Failed to delete announcement",
    onSuccess: () => setDeleteConfirm(null),
    onError: () => setDeleteConfirm(null),
  });

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      headline: announcement.headline,
      content: announcement.content || "",
      linkUrl: announcement.linkUrl || "",
      linkText: announcement.linkText || "",
      isEnabled: announcement.isEnabled,
    });
    setIsDialogOpen(true);
  };

  const isValidUrl = (url: string) => {
    if (!url) return true; // optional field
    try {
      const parsed = new URL(url);
      return parsed.protocol === "https:" || parsed.protocol === "http:";
    } catch {
      return false;
    }
  };

  const linkUrlError =
    formData.linkUrl && !isValidUrl(formData.linkUrl)
      ? "Must be a valid URL starting with https:// or http://"
      : formData.linkUrl && !formData.linkText
      ? ""
      : "";

  const linkTextError =
    formData.linkUrl && !formData.linkText
      ? "Link Text is required when a URL is provided"
      : "";

  const isFormValid =
    !!formData.headline &&
    isValidUrl(formData.linkUrl) &&
    (!formData.linkUrl || !!formData.linkText);

  const handleSubmit = () => {
    if (!isFormValid) return;
    if (editingAnnouncement) {
      updateMutation.mutate({ id: editingAnnouncement._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Announcements</h1>
          <p className="text-muted-foreground">Manage announcements displayed to users</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()}>
              <Plus className="w-4 h-4 mr-2" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingAnnouncement ? "Edit" : "Create"} Announcement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="headline">Headline *</Label>
                <Input
                  id="headline"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  placeholder="Important update"
                />
              </div>
              <div>
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Announcement details..."
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="linkUrl">Link URL</Label>
                  <Input
                    id="linkUrl"
                    value={formData.linkUrl}
                    onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                    placeholder="https://..."
                    className={linkUrlError ? "border-destructive" : ""}
                  />
                  {linkUrlError && (
                    <p className="text-xs text-destructive mt-1">{linkUrlError}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="linkText">Link Text</Label>
                  <Input
                    id="linkText"
                    value={formData.linkText}
                    onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                    placeholder="Learn more"
                    className={linkTextError ? "border-destructive" : ""}
                  />
                  {linkTextError && (
                    <p className="text-xs text-destructive mt-1">{linkTextError}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.isEnabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, isEnabled: checked })}
                />
                <Label>Show announcement to users</Label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={!isFormValid || createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5" />
            <CardTitle>All Announcements</CardTitle>
          </div>
          <CardDescription>{announcements.length} announcements</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Headline</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {announcements.map((announcement) => (
                <TableRow key={announcement._id}>
                  <TableCell className="font-medium">{announcement.headline}</TableCell>
                  <TableCell className="max-w-md truncate">{announcement.content || "-"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${announcement.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {announcement.isEnabled ? "Active" : "Disabled"}
                    </span>
                  </TableCell>
                  <TableCell>{new Date(announcement.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(announcement)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm(announcement._id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {announcements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No announcements yet. Create one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The announcement will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteConfirm && deleteMutation.mutate({ id: deleteConfirm })}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
